/**
 * DO BE RONG VA NGAT DONG - theo dung mo hinh trang thai van ban cua PDF.
 *
 * =============================================================================
 *  CONG THUC BUOC TIEN GLYPH (ISO 32000-1, muc 9.4.4)
 * =============================================================================
 *      tx = ((w0 - Tj/1000) * Tfs + Tc + Tw) * Th/100
 *
 *  trong do:
 *      w0  be rong glyph trong khong gian van ban (don vi 1/1000 em)
 *      Tfs co chu
 *      Tc  gian cach ky tu
 *      Tw  gian cach tu - CHI ap dung cho MA MOT BYTE bang 32
 *      Th  ti le gian ngang, phan tram
 *      Tj  chinh kerning trong mang TJ (ta khong sinh ra, nen bang 0)
 *
 *  Bo qua Tc/Tw/Th la loi kinh dien: chu dat dung o dong dau roi lech dan. Tren
 *  kho tai lieu that, 27.1% run dung kerning tung glyph nen lech rat nhanh.
 *
 *  LUU Y VE Tw VA FONT COMPOSITE: voi font CID (2 byte moi ma), Tw chi ap dung
 *  khi gap DUNG mot byte don gia tri 32 - trong thuc te la khong bao gio. Ban
 *  nay ap Tw cho ky tu khoang trang va ghi nhan day la XAP XI; ban Rust/PDFium
 *  phai phan biet font don gian va font composite o day.
 */
import type { PdfRect, TextAlign, TextRun } from '@pdfeditor/types';
import type { Font } from '../fonts/fontkit';
import { groupRunsIntoLines } from './scope';

/**
 * CAN LE NGANG.
 *
 * "justify" (can deu hai bien) CO CHU DICH khong nam trong kieu nay va se khong
 * duoc them vao. Ly do khong phai la thieu thoi gian:
 *
 *   Can deu doi hoi PHAN PHOI LAI khoang trang giua cac tu, tuc la phat ra mot
 *   gia tri Tw (word spacing) KHAC NHAU cho tung dong. Nhung Tw cua ta dang la
 *   mot phan cua TextState duoc SUY RA TU CHU CU (blockGeometry.ts) va duoc ghi
 *   ra dung mot lan cho ca khoi. Muon can deu thi phai:
 *     - tach Tw ra khoi TextState, phat lai truoc moi dong,
 *     - va do lai be rong tung dong theo Tw moi (measureText phu thuoc Tw),
 *       tuc la mot vong lap hoi quy giua "chon Tw" va "do be rong".
 *   Chua ke voi font composite (CID) thi Tw KHONG ap dung - xem ghi chu o dau
 *   tep - nen can deu se lang le khong co tac dung tren dung nhung tai lieu
 *   tieng Viet dung font nhung 2 byte. Mot nut "can deu" khong lam gi ca la te
 *   hon la khong co nut.
 *
 * DINH NGHIA NAM TRONG HOP DONG (packages/types), khong o day: tu khi lenh
 * 'text.replace' mang duoc `align`, kieu nay di qua ranh gioi hop dong. Hai ban
 * sao cua cung mot union se im lang troi nhau khi mot ben them gia tri moi.
 */
export type { TextAlign };

export interface TextState {
  fontSizePt: number;
  /** Tc */
  charSpacing: number;
  /** Tw */
  wordSpacing: number;
  /** Th, phan tram */
  horizontalScale: number;
}

/** Co chu nho nhat duoc phep thu nho de vua khoi. Duoi muc nay chu khong con doc duoc. */
export const MIN_SHRINK_RATIO = 0.62;

/** Cac buoc thu nho co chu, theo thu tu. Khong nhay thang xuong day. */
const SHRINK_STEPS = [1, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.66, MIN_SHRINK_RATIO];

/**
 * Be rong mot glyph theo don vi 1/1000 em (w0 trong cong thuc tren).
 * Doc tu hmtx/CFF qua fontkit, chuan hoa theo unitsPerEm cua font.
 */
function glyphW0(font: Font, codePoint: number): number {
  try {
    const g = font.glyphForCodePoint(codePoint);
    if (!g) return 0;
    const upem = font.unitsPerEm || 1000;
    return (g.advanceWidth / upem) * 1000;
  } catch {
    return 0;
  }
}

/** Be rong mot chuoi theo diem, ap dung day du Tfs, Tc, Tw, Th. */
export function measureText(font: Font, text: string, state: TextState): number {
  const { fontSizePt: tfs, charSpacing: tc, wordSpacing: tw, horizontalScale: th } = state;
  let total = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    const w0 = glyphW0(font, cp) / 1000;
    // Tw chi ap dung cho ma mot byte bang 32 - xem ghi chu ve font composite o dau file.
    const wordSp = cp === 0x20 ? tw : 0;
    total += (w0 * tfs + tc + wordSp) * (th / 100);
  }
  return total;
}

/**
 * Ngat van ban thanh cac dong vua be rong cho truoc, theo kieu tham lam.
 *
 * Tu dai hon ca dong duoc DE TRAN chu khong cat giua chung: cat mot tu tieng
 * Viet giua chung tao ra chuoi vo nghia. Dong tran se bi tang tren bat lai nhu
 * mot vi pham bbox - to hon la am tham lam hong chu.
 */
export function wrapText(
  font: Font,
  text: string,
  maxWidthPt: number,
  state: TextState,
): string[] {
  const paragraphs = text.split(/\r?\n/);
  const lines: string[] = [];
  for (const para of paragraphs) {
    const words = para.split(/(\s+)/).filter((w) => w.length > 0);
    let current = '';
    for (const w of words) {
      // Khoang trang o dau dong bi bo - no khong bao gio la noi dung.
      if (current === '' && /^\s+$/.test(w)) continue;
      const candidate = current + w;
      if (measureText(font, candidate, state) <= maxWidthPt || current === '') {
        current = candidate;
      } else {
        lines.push(current.replace(/\s+$/, ''));
        current = /^\s+$/.test(w) ? '' : w;
      }
    }
    lines.push(current.replace(/\s+$/, ''));
  }
  return lines.filter((l, i) => l.length > 0 || i === 0);
}

/**
 * Mot dong da duoc DAT XONG: biet chu gi, rong bao nhieu, va bat dau o dau.
 *
 * `xPt` la toa do TUYET DOI trong he toa do trang, da cong `originXPt` cua hop.
 * Duong ghi dua thang no vao setTextMatrix, va ban xem truoc doc `align` tu bao
 * cao chu KHONG duoc tu tinh lai - xem ghi chu ve viec hai ben troi nhau trong
 * coverRect.ts.
 */
export interface PlacedLine {
  text: string;
  widthPt: number;
  xPt: number;
}

export interface LayoutResult {
  ok: boolean;
  lines: string[];
  /** Cung so luong va cung thu tu voi `lines`. */
  placedLines: PlacedLine[];
  /** Can le da duoc DUNG. Bang gia tri suy ra khi ben goi khong chi dinh. */
  align: TextAlign;
  fontSizePt: number;
  lineHeightPt: number;
  shrinkRatio: number;
  neededHeightPt: number;
  widestLinePt: number;
  /** Mep phai xa nhat ma chu se cham toi. Voi can le trai bang xPt + rong nhat. */
  maxRightPt: number;
}

/**
 * Toa do x cua mot dong theo can le, TINH RIENG CHO TUNG DONG bang be rong cua
 * CHINH dong do - khong phai be rong cua dong dai nhat. Dung be rong dong dai
 * nhat cho ca khoi la loi kinh dien: mot khoi can giua se ra thanh mot khoi le
 * trai bi thut vao.
 */
export function lineOriginX(
  originXPt: number,
  boxWidthPt: number,
  lineWidthPt: number,
  align: TextAlign,
): number {
  if (align === 'center') return originXPt + (boxWidthPt - lineWidthPt) / 2;
  if (align === 'right') return originXPt + boxWidthPt - lineWidthPt;
  return originXPt;
}

/**
 * Tim cach dat chu vua trong bbox cua khoi.
 *
 * ADR-005: CHI duoc reflow BEN TRONG khoi. Khong duoc day khoi ben canh xuong,
 * khong duoc tran sang trang sau. Nen khi chu moi dai hon cho, chi co ba lua
 * chon xac dinh: thu nho co chu, gian bbox neu con cho trong (khong lam o ban
 * nay), hoac TU CHOI. Khong bao gio co lua chon "sap xep lai trang".
 */
export function layoutInBox(
  font: Font,
  text: string,
  box: {
    widthPt: number;
    heightPt: number;
    lineHeightPt: number;
    /**
     * Mep TRAI cua hop dat chu, toa do trang. Bo qua = 0, luc do `xPt` cua moi
     * dong la mot DO LECH so voi mep trai va ben goi phai tu cong vao.
     *
     * PHAI la mep trai cua hop bao pham vi (bbox.x), khong duoc la toa do x cua
     * dong chu dau tien. Voi mot tieu de can giua, chu bat dau o GIUA hop, nen
     * lay do lam mep trai roi cong be rong hop se tro toi mot mep phai ao nam
     * ngoai ca kho trang. Cua chan hinh hoc trang bat duoc dieu do, nhung chi
     * sau khi da tu choi mot lan sua le ra hop le.
     */
    originXPt?: number;
  },
  state: TextState,
  align: TextAlign = 'left',
): LayoutResult {
  const originalSize = state.fontSizePt;
  const originX = box.originXPt ?? 0;

  const place = (lines: string[], st: TextState): PlacedLine[] =>
    lines.map((line) => {
      const widthPt = measureText(font, line, st);
      return { text: line, widthPt, xPt: lineOriginX(originX, box.widthPt, widthPt, align) };
    });

  const rightEdge = (placed: readonly PlacedLine[]): number =>
    placed.reduce((m, p) => Math.max(m, p.xPt + p.widthPt), originX);

  for (const ratio of SHRINK_STEPS) {
    const size = originalSize * ratio;
    const st: TextState = { ...state, fontSizePt: size };
    const lineHeight = box.lineHeightPt * ratio;
    const lines = wrapText(font, text, box.widthPt, st);
    const placedLines = place(lines, st);

    // Chieu cao can: dong dau chiem 1 co chu, moi dong sau chiem 1 buoc dong.
    const needed = size + (lines.length - 1) * lineHeight;
    const widest = placedLines.reduce((m, p) => Math.max(m, p.widthPt), 0);

    if (needed <= box.heightPt + 0.5 && widest <= box.widthPt + 0.5) {
      return {
        ok: true,
        lines,
        placedLines,
        align,
        fontSizePt: size,
        lineHeightPt: lineHeight,
        shrinkRatio: ratio,
        neededHeightPt: needed,
        widestLinePt: widest,
        maxRightPt: rightEdge(placedLines),
      };
    }
  }

  // Da thu nho toi han van khong vua.
  //
  // Ta VAN dat chu va van tra ve so do day du, ke ca khi ok=false. Ben goi tu
  // choi dua tren `ok`; con cac con so thi de bao cao noi duoc "can bao nhieu,
  // khoi co bao nhieu". Can le KHONG cuu duoc mot lan sua khong vua: dong qua
  // dai thi can phai hay can giua deu tran ra ngoai het - chi la tran ve phia
  // khac. Cua chan ADR-005 nam o `ok`, khong o day.
  const st: TextState = { ...state, fontSizePt: originalSize * MIN_SHRINK_RATIO };
  const lines = wrapText(font, text, box.widthPt, st);
  const lineHeight = box.lineHeightPt * MIN_SHRINK_RATIO;
  const placedLines = place(lines, st);
  return {
    ok: false,
    lines,
    placedLines,
    align,
    fontSizePt: st.fontSizePt,
    lineHeightPt: lineHeight,
    shrinkRatio: MIN_SHRINK_RATIO,
    neededHeightPt: st.fontSizePt + (lines.length - 1) * lineHeight,
    widestLinePt: placedLines.reduce((m, p) => Math.max(m, p.widthPt), 0),
    maxRightPt: rightEdge(placedLines),
  };
}

// ---------------------------------------------------------------------------
// SUY RA CAN LE CUA CHU CU
// ---------------------------------------------------------------------------

/**
 * VI SAO MAC DINH KHONG DUOC LA 'left'.
 *
 * Tai lieu ma san pham nay nham toi - bao gia, hop dong, quyet dinh tieng Viet -
 * gan nhu luon co tieu de CAN GIUA va cot don gia / thanh tien CAN PHAI. Neu mo
 * trinh soan ra ma mac dinh la can trai thi chi can sua mot ky tu trong tieu de
 * la ca tieu de nhay ve sat mep trai. Nguoi dung khong yeu cau dieu do, khong
 * duoc canh bao, va tren man hinh no trong y het mot loi cua ung dung.
 *
 * Nen mac dinh phai duoc DO tu chu cu, va gia tri do duoc do phai hien len trong
 * giao dien de nguoi dung thay cai ma ta da nhan ra.
 *
 * GIOI HAN CU, VA CACH DA GO BO NO:
 *
 *   Pham vi "Ô" (mot run) co bbox bang dung hop bao cua chinh chu do, nen khong
 *   con khe ho nao de do - moi lan le deu bang 0 va moi o deu ra 'left'. Do
 *   duoc tren tai lieu that: phep suy ra chi phat hien duoc can le o 3 tren 74
 *   khoi, va mot dong tieu de bang CAN GIUA ro rang bang mat van bi doc la
 *   can trai. Ma "Ô" lai la nac MAC DINH khi nguoi dung bam chuot.
 *
 *   Nen ham nay nhan them `container`: hop ma pham vi NAM TRONG, do ben goi
 *   tinh (scope.ts: alignContainerFor). Voi mot o bang, container la khoang
 *   trong giua hai o ben canh - dung cai "ô" ma nguoi dung nhin thay tren giay,
 *   chu khong phai hop bao khit lay chu. Bo qua `container` thi ham cu xu y het
 *   ban truoc: hop do chinh la bbox.
 *
 * Van giu nguyen tac cu: khong co bang chung thi tra ve 'left' vi that su KHONG
 * CO bang chung, chu khong phai vi ta doan la trai.
 */
/**
 * Mot dong bo trong it hon ngan nay (theo ti le be rong hop) thi coi nhu KHONG
 * co bang chung ve can le. Xem giai thich day du trong vong lap ben duoi.
 */
const UNINFORMATIVE_FILL = 0.1;

export function inferAlign(
  runs: readonly TextRun[],
  bbox: PdfRect,
  fontSizePt: number,
  /**
   * Hop CHUA pham vi, dung lam moc do le. Bo qua = dung chinh bbox.
   *
   * CHI dung de DO. Hop DAT CHU van la bbox cua pham vi (xem preflight): noi
   * rong hop dat chu ra ca container se cho phep chu tran sang vung ma ta khong
   * kiem soat duoc - o do co the co khoi khac cua trang. Do le trong mot hop
   * rong hon la an toan; ve chu trong mot hop rong hon thi khong.
   */
  container?: PdfRect,
): TextAlign {
  if (runs.length === 0 || !(bbox.width > 0)) return 'left';
  const box = container && container.width > bbox.width ? container : bbox;

  // Sai so: hop bao chu tu pdf.js khong khit tuyet doi, va chu cai dau/cuoi dong
  // co side bearing khac nhau. 0.2em la duoi muc mot dau cach (~0.26em tren Noto
  // Sans) nen mot khe ho co y nghia van duoc nhin thay.
  const tol = Math.max(1, (fontSizePt > 0 ? fontSizePt : 10) * 0.2);
  const boxLeft = box.x;
  const boxRight = box.x + box.width;

  let left = 0;
  let center = 0;
  let right = 0;

  for (const line of groupRunsIntoLines(runs)) {
    if (line.runs.every((r) => r.text.trim().length === 0)) continue;
    const lineLeft = line.bbox.x;
    const lineRight = line.bbox.x + line.bbox.width;
    const gapLeft = lineLeft - boxLeft;
    const gapRight = boxRight - lineRight;

    // Dong chiem gan het be rong hop thi KHONG NOI LEN DIEU GI ve can le: no
    // trong y het nhau du can trai, can giua hay can phai. Bo qua han thay vi
    // dem no vao phe "trai" - dem no vao la cach mot doan van can giua bi doc
    // thanh can trai chi vi cac dong giua deu day.
    //
    // Nguong phai co CA phan tuyet doi LAN phan tuong doi. Chi dung tol*2
    // (~4pt) la chua du: mot doan van can deu (justified) co THUT DAU DONG
    // TREO - dong dau bat dau o mep hop, cac dong sau thut vao ~18pt - thi moi
    // dong tiep theo co gapLeft=18pt, gapRight=0. Nguong tuyet doi cho no di
    // qua, roi nhanh "gapRight <= tol" dem no la CAN PHAI. Ket qua: mot dieu
    // khoan hop dong can trai bi doc thanh can phai, va sua no se day toan bo
    // 51 dong sang sat mep phai. Da gap that tren hop dong tieng Viet.
    //
    // Do la khoang trong 18/525 = 3.4% be rong hop: mot cai THUT DAU DONG, chu
    // khong phai mot lua chon can le. Mot dong that su can phai (o gia trong
    // cot bang) bo trong nhieu hon the rat nhieu. Nguong 10% tach duoc hai thu.
    if (gapLeft + gapRight <= Math.max(tol * 2, box.width * UNINFORMATIVE_FILL)) continue;

    if (Math.abs(gapLeft - gapRight) <= tol) center++;
    else if (gapRight <= tol) right++;
    else left++;
  }

  if (center > left && center >= right) return 'center';
  if (right > left && right > center) return 'right';
  return 'left';
}
